import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Save, Trash2, Copy, Play, Calendar, FolderPlus, MoreVertical
} from 'lucide-react';
import { BottomSheet } from './BottomSheet';

/**
 * TemplatesSheet - Gestion des templates personnalisés
 */
export function TemplatesSheet({
  isOpen,
  onClose,
  templates,
  onSaveTemplate,
  onApplyTemplate,
  onDeleteTemplate,
  onDuplicateTemplate,
  currentWeekTasks,
  weekDates
}) {
  const [activeTab, setActiveTab] = useState('my'); // 'my' | 'save'
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateDescription, setNewTemplateDescription] = useState('');
  const [selectedIcon, setSelectedIcon] = useState('📋');
  const [editingId, setEditingId] = useState(null);
  const [showMenu, setShowMenu] = useState(null);

  const icons = ['📋', '🎯', '💼', '🏠', '🧘', '💪', '📚', '🎨', '🌟', '⚡'];

  const handleSave = useCallback(() => {
    if (!newTemplateName.trim()) return;

    // Construire les tâches de la semaine
    const weekTasks = {};
    weekDates.forEach((date, idx) => {
      const dateStr = date.toISOString().split('T')[0];
      const tasksForDay = currentWeekTasks.filter(t => t.date === dateStr);
      if (tasksForDay.length > 0) {
        weekTasks[dateStr] = tasksForDay;
      }
    });

    onSaveTemplate(newTemplateName.trim(), weekTasks, {
      description: newTemplateDescription.trim(),
      icon: selectedIcon
    });

    setNewTemplateName('');
    setNewTemplateDescription('');
    setSelectedIcon('📋');
    setActiveTab('my');
  }, [newTemplateName, newTemplateDescription, selectedIcon, currentWeekTasks, weekDates, onSaveTemplate]);

  const handleApply = useCallback((templateId) => {
    onApplyTemplate(templateId);
    onClose();
  }, [onApplyTemplate, onClose]);

  const taskCount = currentWeekTasks?.length || 0;

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title="Mes templates"
      size="large"
    >
      <div className="flex flex-col h-full max-h-[70vh]">
        {/* Tabs */}
        <div className="flex gap-2 px-4 pb-3 border-b border-zinc-800">
          <button
            onClick={() => setActiveTab('my')}
            className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
              activeTab === 'my'
                ? 'bg-zinc-800 text-white'
                : 'text-zinc-500 hover:text-zinc-300'
            }`}
          >
            <Calendar className="w-4 h-4 inline-block mr-2" />
            Mes templates ({templates.length})
          </button>
          <button
            onClick={() => setActiveTab('save')}
            className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
              activeTab === 'save'
                ? 'bg-gradient-to-r from-cyan-600 to-purple-600 text-white'
                : 'text-zinc-500 hover:text-zinc-300'
            }`}
          >
            <Save className="w-4 h-4 inline-block mr-2" />
            Sauvegarder
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          <AnimatePresence mode="wait">
            {activeTab === 'my' ? (
              <motion.div
                key="my"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="space-y-3"
              >
                {templates.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 mx-auto bg-zinc-800/50 rounded-2xl flex items-center justify-center mb-4">
                      <FolderPlus className="w-8 h-8 text-zinc-600" />
                    </div>
                    <p className="text-zinc-400 font-medium mb-1">Aucun template</p>
                    <p className="text-zinc-600 text-sm">
                      Sauvegarde ta semaine actuelle pour la réutiliser
                    </p>
                    <button
                      onClick={() => setActiveTab('save')}
                      className="mt-4 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-sm font-medium rounded-lg transition-all"
                    >
                      Créer un template
                    </button>
                  </div>
                ) : (
                  templates.map(template => (
                    <TemplateCard
                      key={template.id}
                      template={template}
                      onApply={() => handleApply(template.id)}
                      onDelete={() => onDeleteTemplate(template.id)}
                      onDuplicate={() => onDuplicateTemplate(template.id)}
                      showMenu={showMenu === template.id}
                      onToggleMenu={() => setShowMenu(showMenu === template.id ? null : template.id)}
                    />
                  ))
                )}
              </motion.div>
            ) : (
              <motion.div
                key="save"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-4"
              >
                {/* Aperçu */}
                <div className="bg-zinc-800/30 border border-zinc-700/50 rounded-xl p-4">
                  <p className="text-xs font-medium text-zinc-400 uppercase tracking-wide mb-2">
                    Aperçu de ta semaine
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-300">Tâches à sauvegarder</span>
                    <span className="text-lg font-bold text-white">{taskCount}</span>
                  </div>
                  {taskCount === 0 && (
                    <p className="text-xs text-amber-500 mt-2">
                      Ajoute des tâches à ta semaine avant de la sauvegarder
                    </p>
                  )}
                </div>

                {/* Formulaire */}
                <div className="space-y-4">
                  <div>
                    <label className="text-xs font-medium text-zinc-400 uppercase tracking-wide mb-2 block">
                      Nom du template *
                    </label>
                    <input
                      type="text"
                      value={newTemplateName}
                      onChange={(e) => setNewTemplateName(e.target.value)}
                      placeholder="Ex: Ma semaine type"
                      className="w-full bg-zinc-800/50 border border-zinc-700/50 rounded-lg px-4 py-3 text-white placeholder:text-zinc-600 outline-none focus:border-cyan-500/50"
                    />
                  </div>

                  <div>
                    <label className="text-xs font-medium text-zinc-400 uppercase tracking-wide mb-2 block">
                      Description (optionnel)
                    </label>
                    <input
                      type="text"
                      value={newTemplateDescription}
                      onChange={(e) => setNewTemplateDescription(e.target.value)}
                      placeholder="Ex: Planning équilibré travail/repos"
                      className="w-full bg-zinc-800/50 border border-zinc-700/50 rounded-lg px-4 py-3 text-white placeholder:text-zinc-600 outline-none focus:border-cyan-500/50"
                    />
                  </div>

                  <div>
                    <label className="text-xs font-medium text-zinc-400 uppercase tracking-wide mb-2 block">
                      Icône
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {icons.map(icon => (
                        <button
                          key={icon}
                          onClick={() => setSelectedIcon(icon)}
                          className={`w-10 h-10 rounded-lg text-xl flex items-center justify-center transition-all ${
                            selectedIcon === icon
                              ? 'bg-cyan-500/20 ring-2 ring-cyan-500'
                              : 'bg-zinc-800 hover:bg-zinc-700'
                          }`}
                        >
                          {icon}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Bouton sauvegarder */}
                <button
                  onClick={handleSave}
                  disabled={!newTemplateName.trim() || taskCount === 0}
                  className="w-full flex items-center justify-center gap-2 py-3.5 bg-gradient-to-r from-cyan-600 to-purple-600 hover:from-cyan-500 hover:to-purple-500 text-white font-medium rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Save className="w-5 h-5" />
                  Sauvegarder le template
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </BottomSheet>
  );
}

/**
 * TemplateCard - Carte d'un template
 */
function TemplateCard({ template, onApply, onDelete, onDuplicate, showMenu, onToggleMenu }) {
  const createdDate = new Date(template.createdAt).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short'
  });

  return (
    <div className="bg-zinc-800/30 border border-zinc-700/50 rounded-xl p-4 relative">
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className="w-12 h-12 bg-zinc-700/50 rounded-xl flex items-center justify-center text-2xl flex-shrink-0">
          {template.icon}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-zinc-200 truncate">{template.name}</h3>
            <span className="text-xs bg-zinc-700 text-zinc-400 px-2 py-0.5 rounded-full flex-shrink-0">
              {template.taskCount} tâches
            </span>
          </div>
          {template.description && (
            <p className="text-sm text-zinc-500 mt-0.5 truncate">{template.description}</p>
          )}
          <p className="text-xs text-zinc-600 mt-1">Créé le {createdDate}</p>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1">
          <button
            onClick={onApply}
            className="p-2 bg-gradient-to-r from-cyan-600 to-purple-600 hover:from-cyan-500 hover:to-purple-500 text-white rounded-lg transition-all"
            title="Appliquer ce template"
          >
            <Play className="w-4 h-4" />
          </button>
          <div className="relative">
            <button
              onClick={onToggleMenu}
              className="p-2 text-zinc-500 hover:text-zinc-300 hover:bg-zinc-700/50 rounded-lg transition-all"
            >
              <MoreVertical className="w-4 h-4" />
            </button>

            {/* Menu dropdown */}
            <AnimatePresence>
              {showMenu && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="absolute right-0 top-full mt-1 bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl overflow-hidden z-10 min-w-[140px]"
                >
                  <button
                    onClick={() => { onDuplicate(); onToggleMenu(); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-300 hover:bg-zinc-700 transition-colors"
                  >
                    <Copy className="w-4 h-4" />
                    Dupliquer
                  </button>
                  <button
                    onClick={() => { onDelete(); onToggleMenu(); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-red-950/30 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                    Supprimer
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TemplatesSheet;
